<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4 pb-3 border-bottom border-subtle d-flex flex-column flex-md-row justify-content-between align-items-md-center">
      <div>
        <h3 class="text-white font-weight-bold mb-1">
          <i class="mdi mdi-shield-alert text-danger mr-2"></i>{{ $t('quests.consequencesTitle') }}
        </h3>
        <p class="text-muted small mb-0">{{ $t('quests.consequencesSubtitle') }}</p>
      </div>
      <div class="mt-3 mt-md-0">
        <router-link
          :to="{ name: 'CreateConsequence' }"
          class="btn btn-outline-danger btn-sm px-4 font-weight-bold d-inline-flex align-items-center"
        >
          <i class="mdi mdi-plus-circle mr-1"></i>
          {{ $t('quests.createConsequence') }}
        </router-link>
      </div>
    </div>

    <div class="col-12 grid-margin">
      <div class="card shadow-sm">
        <div class="card-body p-4">
          <!-- Search & Filter Bar -->
          <div class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-4 gap-2">
            <div class="search-box" style="max-width: 360px; width: 100%;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-input border-card text-muted">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('quests.searchPlaceholder')"
                />
                <button
                  v-if="search"
                  class="btn btn-outline-secondary"
                  type="button"
                  @click="search = ''"
                >
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <span class="text-white font-weight-bold">{{ filteredConsequence.length }} {{ $currentLocale === 'id' ? 'Aturan' : 'Records' }}</span>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover align-middle" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr class="text-muted small text-uppercase">
                  <th style="width: 40px;">No</th>
                  <th style="width: 240px;">{{ $t('quests.violationRule') }}</th>
                  <th style="width: 160px;">{{ $t('quests.pointsDeduction') }}</th>
                  <th style="width: 340px;">{{ $t('quests.description') }}</th>
                  <th class="text-right" style="width: 110px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedConsequence" :key="data.id">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="overflow-hidden">
                      <router-link
                        :to="{ name: 'DetailConsequence', params: { id: String(data.id) } }"
                        class="text-white font-weight-bold text-decoration-none d-block text-truncate"
                        :title="data.name"
                      >
                        <i class="mdi mdi-alert-circle text-danger mr-1"></i>
                        {{ data.name }}
                      </router-link>
                    </div>
                  </td>
                  <td>
                    <span v-if="data.gold" class="badge badge-danger text-white font-weight-bold mr-1">
                      -{{ Number(data.gold).toLocaleString($currentLocale === 'id' ? 'id-ID' : 'en-US') }} {{ $t('common.gold') }}
                    </span>
                    <span v-if="data.non_gold" class="badge badge-outline-danger font-weight-medium">
                      <i class="mdi mdi-alert-outline mr-1" aria-hidden="true"></i>{{ data.non_gold }}
                    </span>
                    <span v-if="!data.gold && !data.non_gold" class="text-muted small">
                      0 {{ $t('common.gold') }}
                    </span>
                  </td>
                  <td>
                    <span class="text-muted small text-truncate d-block" :title="data.description">{{ data.description || '-' }}</span>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailConsequence', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-info"
                        :title="$t('common.view')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'EditConsequence', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-warning"
                        :title="$t('common.edit')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                      <button
                        type="button"
                        class="btn btn-action-icon btn-outline-danger"
                        @click="handleDelete(data.id)"
                        :title="$t('common.delete')"
                      >
                        <i class="mdi mdi-delete"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedConsequence.length === 0">
                  <td colspan="5" class="text-center py-5">
                    <EmptyState
                      icon="mdi-shield-alert-outline"
                      :title="$t('quests.emptyConsequenceTitle')"
                      :description="$t('quests.emptyConsequenceDesc')"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="filteredConsequence.length > itemsPerPage" class="mt-3">
            <PaginationComponents
              :totalItems="filteredConsequence.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getConsequence, deleteConsequence } from "../../services/consequenceService";
import PaginationComponents from "../../components/PaginationComponents.vue";
import EmptyState from "../../components/EmptyState.vue";
import Swal from "sweetalert2";

export default {
  name: "ConsequenceList",
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: "",
      consequence: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,
    };
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
  },
  computed: {
    filteredConsequence() {
      if (!this.search) return this.consequence;
      const q = this.search.toLowerCase();
      return this.consequence.filter((item) => {
        const nameMatch = (item.name || "").toLowerCase().includes(q);
        const descMatch = (item.description || "").toLowerCase().includes(q);
        return nameMatch || descMatch;
      });
    },
    paginatedConsequence() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredConsequence.slice(start, end);
    },
  },
  async mounted() {
    await this.fetchConsequences();
  },
  methods: {
    async fetchConsequences() {
      this.loading = true;
      try {
        const response = await getConsequence();
        this.consequence = response.data || [];
      } catch (e) {
        // error handled
      } finally {
        this.loading = false;
      }
    },
    async handleDelete(id) {
      const result = await Swal.fire({
        title: this.$t("quests.confirmDelete") || "Delete Penalty Rule?",
        text: this.$t("common.confirmDelete") || "Are you sure you want to delete this penalty rule?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",
        cancelButtonColor: "#6b7280",
        confirmButtonText: this.$t("common.delete") || "Delete",
        cancelButtonText: this.$t("common.cancel") || "Cancel",
        background: "#12141c",
        color: "#fff",
      });
      if (!result.isConfirmed) return;
      try {
        await deleteConsequence(id);
        Swal.fire({
          icon: "success",
          title: this.$t("common.success") || "Success",
          text: this.$t("rulesForm.deletedSuccess") || "Penalty rule deleted successfully.",
          timer: 1800,
          showConfirmButton: false,
          background: "#12141c",
          color: "#fff",
        });
        await this.fetchConsequences();
      } catch (e) {
        Swal.fire({
          icon: "error",
          title: this.$t("common.error") || "Error",
          text: this.$currentLocale === 'id' ? 'Gagal menghapus aturan sanksi SOP.' : 'Failed to delete penalty rule.',
          background: "#12141c",
          color: "#fff",
        });
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
</style>
